import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Token expiry time in hours (must match backend JWT_EXPIRES_IN or be less)
const TOKEN_MAX_AGE_HOURS = 8;

// Helper to check if token is expired based on stored timestamp
const isTokenExpired = () => {
  const tokenTimestamp = localStorage.getItem('adminTokenTimestamp');
  if (!tokenTimestamp) return true;
  
  const tokenAge = Date.now() - parseInt(tokenTimestamp, 10);
  const maxAge = TOKEN_MAX_AGE_HOURS * 60 * 60 * 1000; // Convert hours to milliseconds
  
  return tokenAge > maxAge;
};

// Helper to decode JWT and check expiry (without verification)
const isJWTExpired = (token) => {
  if (!token) return true;
  
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    // exp is in seconds, Date.now() is in milliseconds
    return payload.exp * 1000 < Date.now();
  } catch (e) {
    return true;
  }
};

export const AuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(() => {
    const storedToken = localStorage.getItem('adminToken');
    // Check if token exists and is not expired
    if (storedToken && !isTokenExpired() && !isJWTExpired(storedToken)) {
      return storedToken;
    }
    // Clear expired token
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminTokenTimestamp');
    return null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminTokenTimestamp');
    setToken(null);
    setAdmin(null);
    setError(null);
  }, []);

  const fetchAdmin = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    // Check token expiry before making request
    if (isTokenExpired() || isJWTExpired(token)) {
      console.log('Token expired, logging out');
      logout();
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/admin/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success === false) {
        throw new Error(response.data.error || 'Failed to fetch admin');
      }
      
      setAdmin(response.data.admin);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch admin:', err);
      
      // Check if it's a token expiry error
      if (err.response?.data?.expired || err.response?.status === 401) {
        console.log('Token invalid or expired, logging out');
        logout();
      } else {
        // Don't logout on network errors, just set error state
        setError(err.response?.data?.error || 'Failed to connect to server');
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout]);

  useEffect(() => {
    fetchAdmin();
  }, [fetchAdmin]);

  // Set up interval to check token expiry
  useEffect(() => {
    const checkExpiry = () => {
      if (token && (isTokenExpired() || isJWTExpired(token))) {
        console.log('Token expired during session, logging out');
        logout();
        // Redirect to login page
        window.location.href = '/admin/login';
      }
    };

    // Check every minute
    const interval = setInterval(checkExpiry, 60000);
    
    return () => clearInterval(interval);
  }, [token, logout]);

  const login = async (email, password) => {
    try {
      setError(null);
      console.log('Attempting login for:', email);
      
      const response = await axios.post(`${API_URL}/admin/auth/login`, {
        email,
        password
      });
      
      console.log('Login response:', response.data);
      
      if (response.data.success === false) {
        throw new Error(response.data.error || 'Login failed');
      }
      
      const { token: newToken, admin: adminData } = response.data;
      
      if (!newToken) {
        throw new Error('No token received from server');
      }
      
      // Store token and timestamp
      localStorage.setItem('adminToken', newToken);
      localStorage.setItem('adminTokenTimestamp', Date.now().toString());
      
      setToken(newToken);
      setAdmin(adminData);
      
      console.log('Login successful, admin:', adminData?.name);
      return adminData;
    } catch (err) {
      console.error('Login error details:', err);
      console.error('Error response:', err.response?.data);
      
      let errorMessage = 'Login failed. Please try again.';
      
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const getAuthHeader = () => {
    // Check expiry before returning header
    if (token && !isTokenExpired() && !isJWTExpired(token)) {
      return { Authorization: `Bearer ${token}` };
    }
    return {};
  };

  const isAuthenticated = () => {
    return token && admin && !isTokenExpired() && !isJWTExpired(token);
  };

  return (
    <AuthContext.Provider value={{ 
      admin, 
      token, 
      login, 
      logout, 
      loading, 
      error,
      getAuthHeader, 
      isAuthenticated,
      API_URL 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
